import {
  Controller,
  useFieldArray,
  useForm,
  useFormContext,
} from 'react-hook-form';
import {z} from 'zod';
import {zodResolver} from '@hookform/resolvers/zod';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import ChecklistBox from './components/ChecklistBox';
import {Chip} from '@mui/material';
import {SVG} from '../../../../../../../../constants/assetLists';
import AppDialog from '../../../../../../../../components/common/AppDialog';
import AppSwitch from '../../../../../../../../components/common/AppSwitch';
import MUIIconCheckboxSelect from '../../../../../../../../components/common/MUIIconCheckboxSelect';
import AppTextInput from '../../../../../../../../components/common/AppTextInput';
import AppButton from '../../../../../../../../components/common/AppButton';
import {
  ChecklistFormValues,
  ChecklistListItemFormValues,
  ChecklistSidebarSelection,
  DepartmentFormValues,
  NetworkDetailsFormValues,
  NetworkNodeFieldPath,
} from '../../../../types';
import {ICONS} from '../../../../../../../users/data';

type Props = {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
  selectedFieldPath: NetworkNodeFieldPath;
  editingChecklistId?: string;
  sidebarSelection: ChecklistSidebarSelection | null;
};

export type DepartmentOptionType = {
  label: string;
  value: string;
  icon: string;
};

type DialogFormValues = {
  status: boolean;
  departments: string[];
  title: string;
  description?: string;
  showInBrief: boolean;
  approvalRequired: boolean;
  enableComments: boolean;
  resetDaily: boolean;
  list: {instruction?: string; status: boolean}[];
};

const SWITCHES: {
  label: string;
  name: 'showInBrief' | 'approvalRequired' | 'enableComments' | 'resetDaily';
}[] = [
  {label: 'אפשור הוספת תגובות', name: 'enableComments'},
  {label: 'חובת אישור', name: 'approvalRequired'},
  {label: 'הצגה בבריף', name: 'showInBrief'},
  {label: 'איפוס יומי', name: 'resetDaily'},
];

const dialogZodSchema = z.object({
  status: z.boolean(),
  departments: z
    .array(z.string().min(1, 'מזהה מחלקה לא תקין'))
    .min(1, 'יש לבחור לפחות מחלקה אחת'),
  title: z.string({error: 'שדה חובה!'}).min(3, 'שדה חובה!'),
  description: z.string().optional(),
  showInBrief: z.boolean(),
  approvalRequired: z.boolean(),
  enableComments: z.boolean(),
  resetDaily: z.boolean(),
  list: z
    .array(z.any())
    .min(1, {message: 'שדה חובה!'})
    .refine(
      items => items.some(it => (it.instruction ?? '').trim().length > 0),
      {message: 'לפחות פריט אחד חייב לכלול הוראה לא ריקה'},
    ),
});

const dialogDefaultValues: DialogFormValues = {
  status: true,
  departments: [],
  title: '',
  description: '',
  showInBrief: false,
  approvalRequired: false,
  enableComments: false,
  resetDaily: true,
  list: [],
};

function mapChecklistToDialogForm(
  checklist: ChecklistFormValues,
): DialogFormValues {
  return {
    status: checklist.status,
    departments: checklist.departments,
    title: checklist.title,
    description: checklist.description ?? '',
    showInBrief: checklist.showInBrief,
    approvalRequired: checklist.approvalRequired,
    enableComments: checklist.enableComments,
    resetDaily: checklist.resetDaily,
    list: checklist.list.map((item: ChecklistListItemFormValues) => ({
      instruction: item.instruction ?? '',
      status: item.status,
    })),
  };
}

function mapDialogFormToChecklist(
  formData: DialogFormValues,
  existing?: ChecklistFormValues,
  sidebarSelection?: ChecklistSidebarSelection | null,
): ChecklistFormValues {
  const scopeFields = existing
    ? {shiftId: existing.shiftId, folderId: existing.folderId}
    : sidebarSelection?.type === 'shift'
      ? {shiftId: sidebarSelection.id, folderId: undefined}
      : sidebarSelection?.type === 'folder'
        ? {folderId: sidebarSelection.id, shiftId: undefined}
        : {};

  return {
    id: existing?.id ?? crypto.randomUUID(),
    title: formData.title,
    description: formData.description ?? '',
    status: formData.status,
    departments: formData.departments,
    showInBrief: formData.showInBrief,
    approvalRequired: formData.approvalRequired,
    enableComments: formData.enableComments,
    resetDaily: formData.resetDaily,
    list: formData.list
      .filter(item => item.instruction?.trim())
      .map(item => ({
        instruction: item.instruction,
        status: item.status,
      })),
    ...scopeFields,
  };
}

function AddEditCheckList({
  open,
  onClose,
  onSaved,
  selectedFieldPath,
  editingChecklistId,
  sidebarSelection,
}: Props) {
  const networkForm = useFormContext<NetworkDetailsFormValues>();
  const checklistsPath = `${selectedFieldPath}.checklists` as const;

  const nodeDepartments =
    networkForm.watch(`${selectedFieldPath}.departments`) ?? [];

  const [newlyAddedIndex, setNewlyAddedIndex] = useState<number | null>(null);
  const divRef = useRef<HTMLDivElement | null>(null);

  const {
    formState: {errors, isSubmitting, isValid},
    reset,
    control,
    setValue,
    getValues,
    watch,
    handleSubmit,
  } = useForm<DialogFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: dialogDefaultValues,
    resolver: zodResolver(dialogZodSchema),
  });

  const {
    fields: listFields,
    append: listAppend,
    remove: listRemove,
  } = useFieldArray<DialogFormValues, 'list', 'id'>({
    control,
    name: 'list',
  });

  const departments = watch('departments');

  const departmentOptions = useMemo((): DepartmentOptionType[] => {
    if (nodeDepartments.length === 0) return [];

    const dept = [
      {name: 'כל המחלקות', id: 'all', icon: SVG.saveAllIcon},
      ...nodeDepartments,
    ];

    return dept.map(department => {
      const deptIcon =
        ICONS?.find(icon => icon.id === department.icon)?.icon ||
        SVG.saveAllIcon;

      return {
        label: department.name,
        value: department.id,
        icon: deptIcon,
      };
    });
  }, [nodeDepartments]);

  useEffect(() => {
    if (!open) return;

    if (editingChecklistId) {
      const checklists =
        networkForm.getValues(`${selectedFieldPath}.checklists`) ?? [];
      const existing = checklists.find(
        (c: ChecklistFormValues) => c.id === editingChecklistId,
      );
      if (existing) {
        reset(mapChecklistToDialogForm(existing));
        return;
      }
    }

    reset(dialogDefaultValues);
  }, [open, editingChecklistId, selectedFieldPath, reset, networkForm]);

  const departmentsValue = useMemo(() => {
    if (!departmentOptions.length) return [];
    const selected = departments ?? [];
    const selectedIds =
      selected?.[0] === 'all' ? departmentOptions.map(o => o.value) : selected;

    return departmentOptions.filter(o => selectedIds.includes(o.value));
  }, [departments, departmentOptions]);

  const handleDepartmentsChange = (
    e: React.SyntheticEvent,
    value: {value: string}[] | null,
  ) => {
    const text = e.currentTarget.textContent;
    const isAllSelectedByText = text === 'כל המחלקות';
    const currentDepartments = getValues().departments || [];

    const selectedValues = value?.map(item => item.value) || [];
    const allDeptSelected = nodeDepartments.every(
      (dept: DepartmentFormValues) => selectedValues.includes(dept.id),
    );
    const hasAllInSelection = selectedValues.includes('all');
    const hadAllPreviously = currentDepartments.includes('all');

    let newValue: string[] = [];
    if (hadAllPreviously && !hasAllInSelection) {
      newValue = [];
    } else if (hadAllPreviously) {
      newValue = selectedValues.filter(v => v !== 'all');
    } else if (isAllSelectedByText || hasAllInSelection || allDeptSelected) {
      newValue = ['all'];
    } else {
      newValue = selectedValues;
    }

    setValue('departments', newValue, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const addToCheckList = () => {
    const currentLength = getValues().list?.length || 0;
    listAppend({instruction: '', status: true});
    setNewlyAddedIndex(currentLength);

    requestAnimationFrame(() => {
      if (divRef.current) {
        divRef.current.scrollTop = divRef.current.scrollHeight;
      }
    });
  };

  const handleModalClose = () => {
    reset(dialogDefaultValues);
    setNewlyAddedIndex(null);
    onClose();
  };

  const onSubmit = (formData: DialogFormValues) => {
    const checklists = networkForm.getValues(checklistsPath) ?? [];
    const existingIndex = editingChecklistId
      ? checklists.findIndex(
          (c: ChecklistFormValues) => c.id === editingChecklistId,
        )
      : -1;
    const existing = existingIndex >= 0 ? checklists[existingIndex] : undefined;

    const checklistValues = mapDialogFormToChecklist(
      formData,
      existing,
      sidebarSelection ?? undefined,
    );

    if (existingIndex >= 0) {
      networkForm.setValue(
        `${checklistsPath}.${existingIndex}`,
        checklistValues,
        {shouldValidate: true, shouldDirty: true},
      );
    } else if (sidebarSelection) {
      networkForm.setValue(checklistsPath, [...checklists, checklistValues], {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    onSaved?.();
    handleModalClose();
  };

  const checklistHeaderText = useMemo(() => {
    const list = getValues().list || [];

    if (list.length === 0) return '0 ברשימה';

    const active = list.filter(item => item.status).length;
    const inactive = list.length - active;

    const activeText = active === 1 ? 'פעיל' : 'פעילים';
    const inactiveText = inactive === 1 ? 'כבוי' : 'כבויים';

    if (list.length === 1) {
      return active === 1 ? `1 ${activeText}` : `1 ${inactiveText}`;
    }

    if (active === 0) return `${inactive} ${inactiveText}`;
    if (inactive === 0) return `${active} ${activeText}`;

    return `${active} ${activeText} | ${inactive} ${inactiveText}`;
  }, [watch()]);

  return (
    <AppDialog
      showModel={open}
      onClose={handleModalClose}
      headerTopContent={
        <div className="flex justify-end">
          <div className="flex items-center gap-2">
            <Controller
              name="status"
              control={control}
              render={({field: {onChange, value}}) => (
                <AppSwitch onChange={onChange} checked={value} />
              )}
            />
            <p
              className={`m-0 text-black ${watch('status') ? 'font-medium' : ''}`}>
              צ'קליסט פעיל
            </p>
          </div>
        </div>
      }
      titleField={
        <Controller
          name="title"
          control={control}
          render={({field: {value}}) => (
            <div className="w-full">
              <div className="bg-grayBg px-1 py-2 rounded-tl-[4px] rounded-tr-[4px] border-b-[3px] border-b-linkBlue flex items-center gap-1">
                <input
                  className="w-full m-0 text-[28px] text-black bg-transparent outline-none placeholder:text-black"
                  placeholder={'כותרת צ’קליסט'}
                  autoComplete="off"
                  value={value}
                  onChange={e =>
                    setValue(`title`, e.target.value, {
                      shouldValidate: true,
                      shouldDirty: true,
                      shouldTouch: true,
                    })
                  }
                />
              </div>

              {errors.title?.message && (
                <p className="m-0 text-[12px] text-raspberryRed">
                  {errors.title?.message}
                </p>
              )}
            </div>
          )}
        />
      }
      showHeaderLine={false}
      subTitle={!watch('title') ? "שם הצ'קליסט יופיע בפאנל רשימות" : ''}
      width={924}>
      <div className="flex flex-col gap-2">
        <div
          className="flex-1 flex flex-col gap-3 extraSmall:min-h-[480px] extraSmall:max-h-[480px] xl:min-h-[520px] xl:max-h-[520px] 2xl:min-h-[530px] 2xl:max-h-[530px] overflow-y-auto px-[32px]"
          ref={divRef}>
          <div className="flex gap-3">
            <Controller
              name="departments"
              control={control}
              render={() => (
                <MUIIconCheckboxSelect
                  label="משוייך למחלקה/ות"
                  options={departmentOptions}
                  size="small"
                  fullWidth
                  disableCloseOnSelect
                  id="departments-autocomplete"
                  renderValue={(selectedOptions, getTagProps) => {
                    const allSelected =
                      departments?.[0] === 'all' ||
                      selectedOptions?.length === departmentOptions.length;

                    const renderChip = (
                      option: (typeof departmentOptions)[0],
                      index = 0,
                    ) => (
                      <Chip
                        label={option.label}
                        icon={
                          <img
                            src={option.icon}
                            alt="icon"
                            className="w-[18px] h-[18px] object-contain"
                          />
                        }
                        {...getTagProps({index})}
                      />
                    );

                    if (allSelected) {
                      const allOption = departmentOptions.find(
                        o => o.value === 'all',
                      );
                      return allOption ? renderChip(allOption) : null;
                    }

                    return selectedOptions.map((option, index) =>
                      renderChip(option, index),
                    );
                  }}
                  value={departmentsValue}
                  onChange={handleDepartmentsChange}
                  error={!!errors.departments}
                  helperText={errors.departments?.message}
                />
              )}
            />
          </div>

          <Controller
            name="description"
            control={control}
            render={({field}) => (
              <AppTextInput
                type="text"
                autoComplete="off"
                autoCorrect="off"
                placeholder="הוספת תיאור"
                {...field}
                error={errors.description?.message || ''}
                startIcon={
                  <img
                    src={SVG.descriptionDarkIcon}
                    alt="icon"
                    className="w-full h-full object-contain"
                  />
                }
              />
            )}
          />

          <div className="flex extraSmall:flex-col sm:flex-row sm:justify-between sm:items-center gap-4 pb-2 border-b-2 border-b-tealBlue">
            <p className="m-0 text-[16px] font-semibold text-black">
              {checklistHeaderText}
            </p>

            <div className="flex flex-wrap items-center gap-2">
              {SWITCHES.map(item => (
                <div key={item.name} className="flex items-center gap-2">
                  <Controller
                    name={item.name}
                    control={control}
                    render={({field: {onChange, value}}) => (
                      <AppSwitch onChange={onChange} checked={value} />
                    )}
                  />
                  <p
                    className={`m-0 text-[18px] text-darkerGray ${watch(item.name) ? 'font-medium' : ''}`}>
                    {item.label}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {listFields.map((item, index) => {
            const srNo = (index + 1).toString().padStart(2, '0');
            const shouldAutoFocus = newlyAddedIndex === index;
            return (
              <ChecklistBox
                key={item.id}
                index={index}
                srNo={srNo}
                control={control}
                OnRemove={listRemove}
                setValue={setValue}
                autoFocus={shouldAutoFocus}
              />
            );
          })}

          <div className="flex flex-col gap-1">
            {errors?.list || errors?.list?.length ? (
              <p className="m-0 text-[12px] text-raspberryRed">
                נדרש פריט ברשימה עם טקסט!
              </p>
            ) : null}

            {listFields?.length == 0 && (
              <div className="flex pb-2 border-b border-b-lightDarkGray">
                <button
                  type="button"
                  className="outline-none flex items-center gap-2 px-1 hover:opacity-80 transition-opacity duration-200"
                  onClick={addToCheckList}>
                  <div className="w-[14px] h-[14px]">
                    <img
                      src={SVG.addIcon}
                      alt="icon"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <p className="m-0 text-[20px] font-medium text-gray">
                    הוספה לרשימה
                  </p>
                </button>
              </div>
            )}

            <div className="flex pb-2 border-b border-b-lightDarkGray">
              <button
                type="button"
                className="outline-none flex items-center gap-2 px-1 hover:opacity-80 transition-opacity duration-200"
                onClick={addToCheckList}>
                <div className="w-[14px] h-[14px]">
                  <img
                    src={SVG.addIcon}
                    alt="icon"
                    className="w-full h-full object-contain"
                  />
                </div>
                <p className="m-0 text-[20px] font-medium text-gray">
                  הוספה לרשימה
                </p>
              </button>
            </div>
          </div>
        </div>

        <div className="flex justify-end px-[32px]">
          <div className="flex items-center gap-2">
            <AppButton onClick={handleModalClose}>ביטול</AppButton>

            <AppButton
              variant="primary"
              disabled={!isValid}
              isLoading={isSubmitting}
              onClick={handleSubmit(onSubmit)}>
              שמירה
            </AppButton>
          </div>
        </div>
      </div>
    </AppDialog>
  );
}

export default AddEditCheckList;
